/**
 * Home Page Component
 * Main landing page for Partner Nest Matrimony with enhanced hero section, features, success stories, and motivational content
 * Premium color scheme with gradient backgrounds and smooth animations
 */
import { Link, useNavigate } from 'react-router';
import { Heart, Shield, Users, Star, Eye, ThumbsUp, Play, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import ContactUsModal from '../components/ContactUsModal';
import FAQModal from '../components/FAQModal';
import { useState, useEffect } from 'react';
import './../components/FallingPetals.css';

export default function Home() {
  const [showContactModal, setShowContactModal] = useState(false);
  const [showFAQModal, setShowFAQModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');

  // Dynamically list all images from public/HomeImage folder
  // Note: In production, public folder files need to be explicitly listed
  // Add new images to this array when you add them to the folder
  const heroImages = [
    '/HomeImage/1.png',
    '/HomeImage/2.png',
    '/HomeImage/3.png',
    '/HomeImage/4.png',
    '/HomeImage/5.png',
    '/HomeImage/image1.png'
    // Add more image paths here as you add images to public/HomeImage folder
  ];

  // Image rotation effect - change every 2 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [heroImages.length]);

  const successStories = [
    {
      id: 1,
      coupleNames: "Rahul & Priya",
      location: "Delhi, India",
      story: "We found each other on Partner Nest and connected instantly. Our families supported our decision to marry without dowry, and we've never been happier!",
      marriedDate: "Married 2023",
      duration: "2 years together",
      image: "https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/fd77eb98-0455-41ae-b7a2-cb98265d2b86.jpg"
    },
    {
      id: 2,
      coupleNames: "Amit & Sneha",
      location: "Mumbai, India",
      story: "Partner Nest helped us find true love based on compatibility, not financial demands. Our marriage is built on mutual respect and understanding.",
      marriedDate: "Married 2022",
      duration: "3 years together",
      image: "https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/f8ed3851-9aac-440c-b871-f3a91ecaea75.jpg"
    },
    {
      id: 3,
      coupleNames: "Vikram & Anjali",
      location: "Bangalore, India",
      story: "We're grateful to Partner Nest for bringing us together. Our love story proves that dowry-free marriages are not just possible, but beautiful!",
      marriedDate: "Married 2024",
      duration: "6 months together",
      image: "https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/915ed65b-b53a-4a42-b5cc-23467c8bf3c2.jpg"
    }
  ];

  const motivationalVideos = [
    {
      id: 1,
      title: "Breaking the Chains of Dowry",
      thumbnail: "https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/ac3773d9-7cba-467d-bff1-16c25bd893ea.jpg",
      views: "15.2K",
      likes: "2.1K",
      duration: "4:30",
      videoUrl: "/HomeVideos/Video1.mp4"
    },
    {
      id: 2,
      title: "Love Without Price Tag",
      thumbnail: "https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/1564f656-4858-4909-bee1-11f925d2df51.jpg",
      views: "12.8K",
      likes: "1.8K",
      duration: "3:45",
      videoUrl: "/HomeVideos/Video2.mp4"
    },
    {
      id: 3,
      title: "Empowering Women, Ending Dowry",
      thumbnail: "https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/a77a0b1b-cdc5-4589-b548-bb56f087e72f.jpg",
      views: "18.5K",
      likes: "2.7K",
      duration: "5:15",
      videoUrl: "/HomeVideos/Video3.mp4"
    },
    {
      id: 4,
      title: "Modern Relationships, Traditional Values",
      thumbnail: "https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/57414d23-0147-4625-9cf5-caaffd31be55.jpg",
      views: "9.7K",
      likes: "1.3K",
      duration: "4:10",
      videoUrl: "/HomeVideos/Video4.mp4"
    }
  ];

  const navigate = useNavigate();

  const handleBrowsePartners = (e: React.MouseEvent) => {
    e.preventDefault();
    // Consider user logged in if authToken or userData exists in localStorage
    const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
    const user = typeof window !== 'undefined' ? localStorage.getItem('userData') || localStorage.getItem('user') : null;
    const isAuthenticated = Boolean(token || user);

    if (!isAuthenticated) {
      // Required message when user is not logged in
      alert('To check valid profiles, either Login or Register');
      return;
    }

    // Navigate to partner search
    navigate('/search');
  };

  const features = [
    {
      icon: Shield,
      title: "Trusted Matchmaking for Modern Hearts",
      description: "Redefining modern matchmaking with trusted connections and meaningful relationships"
    },
    {
      icon: Heart,
      title: "Genuine Connections",
      description: "Focus on compatibility, values, and mutual respect over material demands"
    },
    {
      icon: Users,
      title: "Verified Profiles",
      description: "All profiles are thoroughly verified to ensure authenticity and safety"
    },
    {
      icon: Star,
      title: "Premium Matching",
      description: "Advanced algorithms to find your perfect life partner"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Premium Hero Section with Logo Color Palette */}
      <section className="relative overflow-hidden pt-10 pb-20 bg-gradient-to-br from-[#0b3b82] via-[#5f1c7c] to-[#ff2d87]">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-72 h-72 rounded-full bg-[#ffd9a6]/15 blur-3xl"></div>
          <div className="absolute top-16 right-0 w-56 h-56 rounded-full bg-white/10 blur-3xl"></div>
          <div className="absolute bottom-16 left-1/4 w-60 h-60 rounded-full bg-[#ff2d87]/20 blur-3xl"></div>
          <div className="absolute inset-x-0 bottom-0 h-32 bg-[radial-gradient(circle_at_bottom,_rgba(255,217,166,0.25),transparent_62%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(255,255,255,0.08),transparent_20%),radial-gradient(circle_at_bottom_right,_rgba(255,210,100,0.1),transparent_18%)]"></div>
        </div>
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(12)].map((_, idx) => (
              <div
                key={idx}
                className={`hero-particle ${idx % 3 === 0 ? 'hero-particle--slow' : idx % 3 === 1 ? 'hero-particle--medium' : 'hero-particle--fast'} ${idx % 2 === 0 ? 'hero-particle--gold' : 'hero-particle--rose'}`}
                style={{
                  top: `${8 + (idx * 6) % 80}%`,
                  left: `${10 + (idx * 13) % 75}%`,
                  width: `${12 + (idx % 4) * 6}px`,
                  height: `${12 + (idx % 5) * 5}px`,
                  animationDelay: `${idx * 0.6}s`,
                }}
              />
            ))}
          </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] items-start">
            <div className="text-center lg:justify-center">
              <span className="inline-flex items-center justify-center rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.35em] text-[#ffd9a6] shadow-sm">
                India’s Trusted Matchmaking Destination, Modern Connections, Traditional Values
              </span>
              <div className="mt-8 flex justify-center lg:justify-start">
                <div className="relative w-full max-w-[680px] overflow-hidden rounded-[3rem] border border-white/20 bg-white/10 shadow-[0_45px_120px_-60px_rgba(255,255,255,0.75)] backdrop-blur-xl">

                  <div className="pointer-events-none absolute inset-0 rounded-[3rem] bg-[radial-gradient(circle_at_center,_rgba(255,217,166,0.28),transparent_40%)]" />

                  <div className="pointer-events-none absolute -inset-6 rounded-[3.5rem] bg-[#ffd9a6]/15 blur-3xl" />

                  <img
                    src="/images/PartnerNestTitle.png"
                    alt="Partner Nest"
                    className="relative z-10 w-full h-[200px] md:h-[240px] lg:h-[300px] xl:h-[340px] object-cover"
                    loading="eager"

/>

                  <div className="pointer-events-none absolute inset-0 rounded-[3rem] border border-[#ffd9a6]/30" />
                </div>
</div>
              <p className="mt-8 max-w-3xl text-xl sm:text-2xl leading-relaxed text-[#f8e2ae]">
                Experience the world-class luxury of a best-in-class matrimonial platform where elegance, trust and meaningful matches come together for a life-changing relationship.
              </p>
              <p className="mt-4 max-w-3xl text-base sm:text-lg leading-relaxed text-[#ffecce]">
                Curated profiles. Intelligent introductions. A premium journey crafted for modern families seeking meaningful love.
              </p>

              {/* stats (moved below to span full hero width) */}
            </div>

            <div className="relative flex justify-center lg:justify-end">
              <div className="relative w-full max-w-md lg:translate-x-16 xl:translate-x-20 lg:mt-4">
                <div className="absolute -inset-6 rounded-full bg-[#ffd9a6]/20 blur-3xl"></div>
                <div className="relative overflow-hidden rounded-[2.5rem] border border-white/10 bg-white/10 p-5 shadow-2xl backdrop-blur-xl">
                  <div className="relative overflow-hidden rounded-[2rem]">
                    <img
                      src={heroImages[currentImageIndex]}
                      alt="Happy Couple"
                      className="w-full aspect-square object-cover"
                      key={currentImageIndex}
                    />
                    <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#0b3b82]/80 via-transparent to-transparent"></div>
                  </div>
                  <div className="absolute top-4 left-4 rounded-full bg-white/15 px-4 py-2 text-xs uppercase tracking-[0.18em] text-[#ffd9a6] shadow-sm">
                    Premium Match
                  </div>
                  <div className="absolute -right-8 top-1/2 hidden sm:block">
                    <div className="h-28 w-28 rounded-full border border-[#ffd9a6]/60 bg-[#ff2d87]/20 blur-xl"></div>
                  </div>
                </div>
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link to="/register">
                    <Button className="bg-[#ffd9a6] text-[#0b3b82] hover:bg-[#ffe0a2] text-lg px-10 py-3 rounded-full font-semibold shadow-2xl transition-all duration-300 transform hover:-translate-y-0.5">
                      Create Profile for Free
                    </Button>
                  </Link>
                  <Button onClick={handleBrowsePartners} variant="outline" className="rounded-full border-white/70 bg-white/10 px-10 py-3 text-white text-lg font-semibold shadow-lg hover:bg-white hover:text-[#0b3b82] transition-all duration-300">
                    Browse Partners
                  </Button>
                </div>
              </div>
            </div>

            <div className="col-span-full">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-start justify-between gap-6 mt-8 overflow-x-auto">
                  <div className="min-w-[220px] w-64 rounded-[1.75rem] border border-white/10 bg-white/10 p-5 shadow-lg backdrop-blur-xl flex-shrink-0">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-white">24K+</p>
                      <p className="mt-2 text-xs uppercase tracking-[0.18em] text-[#ffd9a6]">Success Stories</p>
                    </div>
                  </div>

                  <div className="min-w-[220px] w-64 rounded-[1.75rem] border border-white/10 bg-white/10 p-5 shadow-lg backdrop-blur-xl flex-shrink-0 mx-auto">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-white">98%</p>
                      <p className="mt-2 text-xs uppercase tracking-[0.18em] text-[#ffd9a6]">Verified Matches</p>
                    </div>
                  </div>

                  <div className="min-w-[220px] w-64 rounded-[1.75rem] border border-white/10 bg-white/10 p-5 shadow-lg backdrop-blur-xl flex-shrink-0">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-white">Global</p>
                      <p className="mt-2 text-xs uppercase tracking-[0.18em] text-[#ffd9a6]">Trusted Community</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Partner Nest Matrimony Section */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-[#0b3b82]/95 via-[#5f1c7c]/95 to-[#ff2d87]/95">
        {/* Decorative Gradients */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 left-5 w-56 h-56 rounded-full bg-[#ffd9a6] blur-3xl"></div>
          <div className="absolute bottom-10 right-5 w-64 h-64 rounded-full bg-[#ff2d87]/40 blur-3xl"></div>
          <div className="absolute top-1/2 left-1/3 w-48 h-48 rounded-full bg-white/10 blur-2xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-[#ffd9a6] via-white to-[#ff2d87] bg-clip-text text-transparent">
              Why Choose Partner Nest Matrimony?
            </h2>
            <br></br>
            <p className="text-lg text-white/90 max-w-3xl mx-auto leading-relaxed">
              Connecting hearts through meaningful relationships, shared values, and lifelong compatibility.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-lg border border-[#ffd9a6]/30 rounded-2xl p-8 shadow-xl hover:shadow-2xl hover:bg-white/15 transition-all duration-300 transform hover:-translate-y-1 group">
                <div className="w-14 h-14 bg-gradient-to-br from-[#ffd9a6] to-[#ff2d87] rounded-lg flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                  <feature.icon className="w-7 h-7 text-[#0b3b82]" />
                </div>
                <h3 className="text-xl font-bold text-[#ffd9a6] mb-3">{feature.title}</h3>
                <p className="text-white/80 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories Section */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-[#5f1c7c] via-[#0b3b82] to-[#ff2d87]">
        {/* Decorative Gradients */}
        <div className="absolute inset-0 opacity-25">
          <div className="absolute top-10 left-10 w-56 h-56 rounded-full bg-[#ffd9a6] blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-64 h-64 rounded-full bg-[#ff2d87]/30 blur-3xl"></div>
          <div className="absolute top-1/3 right-1/4 w-48 h-48 rounded-full bg-white/10 blur-2xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-[#ffd9a6] via-white to-[#ff2d87] bg-clip-text text-transparent">
              Success Stories
            </h2>
            <p className="text-lg text-white/90 max-w-3xl mx-auto leading-relaxed">
              Real couples who found love without dowry through our platform — their stories inspire us every day
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {successStories.map((story) => (
              <div key={story.id} className="bg-white/10 backdrop-blur-lg border border-[#ffd9a6]/30 rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={story.image} 
                    alt={story.coupleNames}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0b3b82]/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="text-xl font-semibold">{story.coupleNames}</h3>
                    <p className="text-[#ffd9a6]">{story.location}</p>
                  </div>
                </div>
                
                <div className="p-6">
                  <p className="text-white/80 mb-4 italic">"{story.story}"</p>
                  <div className="flex items-center justify-between text-sm text-[#ffd9a6]">
                    <span className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      {story.marriedDate}
                    </span>
                    <span className="flex items-center">
                      <Heart className="w-4 h-4 mr-1 text-[#ff2d87]" />
                      {story.duration}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Button variant="outline" className="bg-white/10 border-[#ffd9a6] text-[#ffd9a6] hover:bg-white/20">
              View More Success Stories
            </Button>
          </div>
        </div>
      </section>

      {/* Footer Section */}
      <section className="py-16 relative overflow-hidden bg-gradient-to-br from-[#0b3b82] via-[#2d1b4e] to-[#5f1c7c]">
        {/* Decorative Gradients */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-40 h-40 rounded-full bg-[#ffd9a6] blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-48 h-48 rounded-full bg-[#ff2d87]/30 blur-3xl"></div>
          <div className="absolute top-1/2 left-1/3 w-36 h-36 rounded-full bg-white/10 blur-2xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Company Info */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-[#ffd9a6]">Partner Nest Matrimony</h3>
              <p className="text-white/80 text-sm leading-relaxed">
                Partner Nest - Building relationships based on love, respect, and compatibility, not material demands.
              </p>
              <div className="flex space-x-4">
                <div className="w-8 h-8 bg-[#ffd9a6] rounded-full flex items-center justify-center">
                  <span className="text-[#0b3b82] text-xs font-bold">F</span>
                </div>
                <div className="w-8 h-8 bg-[#ffd9a6] rounded-full flex items-center justify-center">
                  <span className="text-[#0b3b82] text-xs font-bold">T</span>
                </div>
                <div className="w-8 h-8 bg-[#ffd9a6] rounded-full flex items-center justify-center">
                  <span className="text-[#0b3b82] text-xs font-bold">I</span>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-[#ffd9a6]">Quick Links</h3>
              <ul className="space-y-2 text-white/80">
                <li><Link to="/" className="hover:text-[#ffd9a6] transition-colors">Home</Link></li>
                <li><Link to="/search" className="hover:text-[#ffd9a6] transition-colors">Browse Profiles</Link></li>
                <li><Link to="/register" className="hover:text-[#ffd9a6] transition-colors">Create Profile</Link></li>
                <li><Link to="/login" className="hover:text-[#ffd9a6] transition-colors">Member Login</Link></li>
                <li><Link to="/success-stories" className="hover:text-[#ffd9a6] transition-colors">Success Stories</Link></li>
              </ul>
            </div>

            {/* Help & Support */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-[#ffd9a6]">Help & Support</h3>
              <ul className="space-y-2 text-white/80">
                <li>
                  <button 
                    onClick={() => setShowContactModal(true)}
                    className="hover:text-[#ffd9a6] transition-colors text-left"
                  >
                    Contact Us
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => setShowFAQModal(true)}
                    className="hover:text-[#ffd9a6] transition-colors text-left"
                  >
                    FAQ
                  </button>
                </li>
                <li><Link to="/safety-tips" className="hover:text-[#ffd9a6] transition-colors">Safety Tips</Link></li>
                <li><Link to="/customer-care" className="hover:text-[#ffd9a6] transition-colors">Customer Care</Link></li>
                <li><Link to="/feedback" className="hover:text-[#ffd9a6] transition-colors">Feedback</Link></li>
              </ul>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-[#ffd9a6]">Contact Us</h3>
              <div className="space-y-2 text-white/80 text-sm">
                <p>📞 +91 9491702821</p>
                <p>📧 rjbdmat@gmail.com</p>
                <p>📍 12-2-683, Navodaya Colony, Mehdipatnam, Hyderabad, India</p>
                <p>🕒 24/7 Customer Support</p>
              </div>
            </div>
          </div>

          {/* Final CTA */}
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-[#ffd9a6] to-[#ff2d87] bg-clip-text text-transparent mb-4">
              Ready to Start Your Journey?
            </h2>
            <p className="text-white/80 mb-6">
              Join thousands of happy couples who found love without dowry
            </p>
            <Link to="/register">
              <Button className="bg-[#ffd9a6] hover:bg-[#ffe0a2] text-[#0b3b82] text-lg px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300">
                Create Your Free Profile
              </Button>
            </Link>
          </div>

          {/* Copyright & Policies */}
          <div className="border-t border-white/20 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-white/70 text-sm">
                © 2026 Partner Nest Matrimony. All rights reserved.
              </div>
              <div className="flex flex-wrap justify-center gap-6 text-white/70 text-sm">
                <Link to="/privacy-policy" className="hover:text-[#ffd9a6] transition-colors">Privacy Policy</Link>
                <Link to="/terms-conditions" className="hover:text-[#ffd9a6] transition-colors">Terms & Conditions</Link>
                <Link to="/refund-policy" className="hover:text-[#ffd9a6] transition-colors">Refund Policy</Link>
                <Link to="/disclaimer" className="hover:text-[#ffd9a6] transition-colors">Disclaimer</Link>
                <Link to="/sitemap" className="hover:text-[#ffd9a6] transition-colors">Sitemap</Link>
                <Link to="/admin-login" className="hover:text-[#ffd9a6] transition-colors font-medium">Admin Login</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Us Modal */}
      <ContactUsModal open={showContactModal} onClose={() => setShowContactModal(false)} />

      {/* FAQ Modal */}
      <FAQModal open={showFAQModal} onClose={() => setShowFAQModal(false)} />

      {/* Video Player Modal */}
      {showVideoModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
          onClick={() => setShowVideoModal(false)}
        >
          <div 
            className="relative w-full max-w-4xl bg-black rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all"
            >
              <span className="text-white text-2xl font-bold">×</span>
            </button>

            {/* Video Player */}
            <video
              src={currentVideoUrl}
              controls
              autoPlay
              className="w-full h-auto"
              style={{ maxHeight: '80vh' }}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      )}
    </div>
  );
}